import { createContext, useContext } from "react";

export type Banner = {
  buttonText: string;
  buttonUrl: string;
  content: readonly {
    html: string;
  }[];
  heading: string;
  dek?: string;
  showOnHomepage?: boolean;
} | null;

type SiteLogoImage = {
  url: string;
  width: number;
  height: number;
};
export type SiteLogo = {
  social: SiteLogoImage;
  original: SiteLogoImage;
};

export type SEO = {
  longSiteTitle?: string;
  longSiteDescription?: string;
  facebookAppId?: string;
  googleSiteVerification?: string;
  diversityPolicyUrl?: string;
  ethicsPolicyUrl?: string;
  founders: string[];
  foundingDate?: string;
  knowsAbout: string[];
  linkedInUrl?: string;
  mastheadUrl?: string;
  wikipediaUrl?: string;
};

export type SiteSettings = {
  siteSlug: string;
  siteTitle: string;
  siteDomain: string;
  siteTagline?: string;
  placeholderImageUrl: {
    horizontal: string;
    vertical: string;
    square: string;
    social: string;
  };
  socialMediaLinks: readonly {
    platformName: string;
    url: string;
  }[];
  siteLogo: SiteLogo | null;
  seo: SEO | null;
};

export type Newsletter = {
  title: string;
  slug: string;
  listId: string;
  default: boolean;
  promoText: {
    html: string;
  };
  frequency: string;
  image: {
    thumbnails: {
      original: {
        url: string;
      };
    };
  } | null;
};

export interface SubNavItem {
  title: string;
  url: string;
  target?: "_blank" | "_self" | "_parent" | "_top";
}

export interface NavItem {
  title: string;
  url: string;
  special?: boolean;
  target?: "_blank" | "_self" | "_parent" | "_top";
  defaultOpen?: boolean;
  icon?: React.ReactElement;
  allLink?: {
    title: string;
    url: string;
    target?: "_blank" | "_self" | "_parent" | "_top";
  };
  subItems?: ReadonlyArray<SubNavItem>;
}

export interface GlobalDataContextType {
  breakingNewsBanner: Banner;
  wikiBanner: Banner;
  siteSettings: SiteSettings;
  newsletters: readonly Newsletter[] | null;
  mainNav: NavItem[] | null;
  footerNav: NavItem[] | null;
}

const GlobalDataContext = createContext<GlobalDataContextType | null>(null);

export const useGlobalDataContext = (): GlobalDataContextType => {
  const globalDataContext = useContext(GlobalDataContext);

  if (!globalDataContext) {
    throw new Error("Missing Global Data Context");
  }

  return globalDataContext;
};

export default GlobalDataContext;
